import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Grid } from '@material-ui/core';

import { constants as appClientConstants, core as appClientCore, utils as appClientUtils } from '@edf-pkg/app-client';
import { useSnackbar } from '@edf-pkg/app-main';

import useErrorHandler from '$app-web/hooks/use-error-handler';
import usePage from '$app-web/hooks/use-page';
import Logo from '$app-web/modules/auth/components/logo';
import Alert from '$app-web/ui-kit/components/alert';
import { URLsManager } from '$app-web/utils';

import { useAccountLock, useSignIn } from '../../hooks';
import Form from './components/form';
import FormWrapper from './components/form-wrapper';
import useStyles from './styles';

const PageSignIn = () => {
    const { t } = useTranslation();
    const { showError } = useSnackbar();
    const history = useHistory();
    const { handleError } = useErrorHandler();
    const classes = useStyles();

    const { accountLockedState, handleLockAccount, closeAccountLockedAlert, resetAccountLockedState } = useAccountLock();
    const [showPasswordExpiryAlert, setShowPasswordExpiryAlert] = useState(false);
    const [showAutoLogoutAlert, setShowAutoLogoutAlert] = useState(appClientUtils.auth.isAutoLogoutOccurred());

    const { signIn, isMFAEnabled } = useSignIn({
        onError: (error) => {
            if (error.innerError.data.errorCode === appClientCore.ERROR_CODES.PASSWORD_IS_EXPIRED) {
                setShowPasswordExpiryAlert(true);
            } else if (error.innerError.data.errorCode === appClientCore.ERROR_CODES.ACCOUNT_IS_LOCKED) {
                const retryAfterInSeconds = error.innerError.headers?.['retry-after'];
                handleLockAccount(retryAfterInSeconds);
            } else if (error.innerError.data.errorCode === appClientCore.ERROR_CODES.UNAUTHORIZED_OR_INACTIVE) {
                showError(t('pdash:login_failed_invalid_username_password'));
            } else {
                resetAccountLockedState();
                handleError(error);
            }
        },
        silentError: true,
    });

    usePage(t('pdash:login_button_text'));

    const closeAutoLogoutAlert = () => {
        setShowAutoLogoutAlert(false);
        appClientUtils.auth.setAutoLogoutLocalStorageFlag(false);
    };

    const closePasswordExpiryAlert = () => {
        setShowPasswordExpiryAlert(false);
    };

    const redirectToForgetPasswordPage = () => {
        history.push(URLsManager.to('dashboard:auth:FORGOT_PASSWORD'));
    };

    return (
        <>
            <Grid item>
                <Logo />
            </Grid>
            <Grid item className={classes.signInFormWrapper}>
                <FormWrapper onSubmit={signIn}>
                    <Form isMFAEnabled={isMFAEnabled} signIn={signIn} />
                </FormWrapper>
            </Grid>
            {showAutoLogoutAlert && (
                <div className={classes.alertWrapper}>
                    <Alert onClose={closeAutoLogoutAlert} content={t('pdash:auto_logout_occurred_notification_content')} />
                </div>
            )}
            {showPasswordExpiryAlert && (
                <div className={classes.alertWrapper}>
                    <Alert
                        type="warning"
                        onClose={closePasswordExpiryAlert}
                        title={t('pdash:password_has_expired_notification_title')}
                        content={t('pdash:password_has_expired_notification_content')}
                        action={{
                            onClick: redirectToForgetPasswordPage,
                            title: t('pdash:reset_password'),
                        }}
                    />
                </div>
            )}
            {accountLockedState.showAlert && (
                <div className={classes.alertWrapper}>
                    <Alert
                        type="error"
                        onClose={closeAccountLockedAlert}
                        content={
                            accountLockedState.minutesLeftUntilUnlock === appClientConstants.ACCOUNT_LOCKED_TIME_IN_MINUTES
                                ? t('pdash:account_is_locked_alert_description_with_support_email', [
                                      accountLockedState.minutesLeftUntilUnlock,
                                  ])
                                : t('pdash:account_is_locked_alert_description', [accountLockedState.minutesLeftUntilUnlock])
                        }
                    />
                </div>
            )}
        </>
    );
};

export default PageSignIn;
